// src/views/whatsappInbox/ChatLeftMenuRedux.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "antd";
import { toast } from "react-hot-toast";
import { clearInputValueById } from "../../hooks/use-debounce";
import InboxListRedux from "./InboxListRedux";
import AddChat from "./AddChat";
import FilterDropdown from "./FilterDropdown";
import ConversationBulkActionsBar from "./ConversationBulkActionsBar";
// Removed BulkSelector - using inline selection UI instead
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { ArrowUp, Search, X, CheckSquare } from "lucide-react";
import "./ChatLeftMenuRedux.css";
import {
  fetchConversations,
  selectAllConversations,
  selectConversationsStatus,
  selectCurrentPage,
  selectFilters,
  selectSelectedConversation,
  selectTotalConversations,
  setCurrentPage,
  setFilter,
  setSelectedConversation,
  startNewChat,
  toggleFilter,
  clearFilters,
  // Bulk selection imports
  selectIsSelectionMode,
  selectSelectedCount,
  selectBulkActionStatus,
  toggleSelectionMode,
  clearConversationSelection,
  selectNextConversations,
  User
} from "../../store/slices/conversationsSlice";
import { setShowAddChatModal, selectShowAddChatModal } from "../../store/slices/uiSlice";
import { Logger } from '../../utils/logger';
import debounce from "lodash/debounce";


interface ChatLeftMenuReduxProps {
    usersArray?: any[]; // For compatibility during migration
    selectedUser?: any;
    onConnect?: (user: any) => void; 
    listInnerRef?: React.RefObject<any>;
    filter?: any;
  }

  const ChatLeftMenuRedux: React.FC<ChatLeftMenuReduxProps> = ({
    usersArray,
    filter,
    selectedUser,
     onConnect, 
    
  }) => {
  // Redux hooks
  const dispatch = useAppDispatch();
  const conversations = useAppSelector(selectAllConversations);
  const selectedConversation = useAppSelector(selectSelectedConversation);
  const filters = useAppSelector(selectFilters);
  const isLoading = useAppSelector(selectConversationsStatus) === 'loading';
  const showAddChatModal = useAppSelector(selectShowAddChatModal);
  const currentPage = useAppSelector(selectCurrentPage);
  const totalConversations = useAppSelector(selectTotalConversations);

  // Bulk selection state
  const isSelectionMode = useAppSelector(selectIsSelectionMode);
  const selectedCount = useAppSelector(selectSelectedCount);
  const bulkActionStatus = useAppSelector(selectBulkActionStatus);
  const isBulkLoading = bulkActionStatus === 'loading';
  
  const defaultListRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const debouncedFunctionRef = useRef<any>(null);
  const previousFiltersRef = useRef(filters);

  // Local state (for compatibility with existing components)
  const [showAddChat, setShowAddChat] = useState(false);

  // Refs
  const listInnerRef = useRef<any>(null);

  // Logging for debugging
  Logger.log("ChatLeftMenuRedux rendered with conversations:", conversations?.length);

  // Initial fetch is handled by parent WhatsappInbox component
  // No need to duplicate the call here
  

// ✅ UPDATED: Better debounce setup with cleanup
useEffect(() => {
  debouncedFunctionRef.current = debounce((value: string) => {
    Logger.log("Setting search filter (debounced):", { key: 'search', value });
    // ✅ This will trigger the filter useEffect above
    dispatch(setFilter({ key: 'search' as any, value }));
  }, 500); // ✅ Increased debounce time for better performance

  return () => {
    // ✅ Enhanced cleanup
    if (debouncedFunctionRef.current?.cancel) {
      debouncedFunctionRef.current.cancel();
    }
  };
}, [dispatch]);

// ✅ NEW: Cleanup filters when component unmounts
useEffect(() => {
  return () => {
    Logger.log("ChatLeftMenuRedux unmounting, clearing filters");
    dispatch(clearFilters());
  };
}, [dispatch]);

// ✅ UPDATED: Sync local searchValue with Redux filter
useEffect(() => {
  // Keep local searchValue in sync with Redux filter
  setSearchValue(filters.search || '');
}, [filters.search]);

// ✅ UPDATED: Better filter effect that handles both setting and clearing filters
useEffect(() => {
  const previousFilters = previousFiltersRef.current;
  const currentFilters = filters;

  // Check if filters actually changed (not just initial render)
  const filtersChanged = JSON.stringify(previousFilters) !== JSON.stringify(currentFilters);

  if (filtersChanged) {
    Logger.log("Filters changed, fetching conversations with RESET pagination", {
      previous: previousFilters,
      current: currentFilters
    });

    // Always fetch when filters change - whether setting or clearing
    dispatch(fetchConversations({ page: 1, refresh: true }));

    // Update the ref for next comparison
    previousFiltersRef.current = currentFilters;
  }
}, [filters, dispatch]);

// ✅ UPDATED: Improved handleFilter with proper debouncing
const handleFilter = (input: any) => {
  const { name, value } = input.target;
  
  if (name === 'search') {
    // ✅ Update local state immediately for UI responsiveness
    setSearchValue(value);
    
    // ✅ Cancel previous debounced call
    if (debouncedFunctionRef.current?.cancel) {
      debouncedFunctionRef.current.cancel();
    }
    
    // ✅ Trigger debounced search
    debouncedFunctionRef.current(value);
  } else {
    // ✅ Non-search filters trigger immediate action
    dispatch(setFilter({ key: name as any, value }));
  }
};

  const handleTagFilter = (filterKey: string) => {
    dispatch(toggleFilter(filterKey as any));
    Logger.log("Toggling filter:", filterKey);
  };

  const handleRemoveFilter = (filterKey: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent the parent button's onClick from firing
    dispatch(setFilter({ key: filterKey as any, value: undefined })); // Remove the filter
    Logger.log("Removing filter:", filterKey);
  };

  const handleConnect = (user: User) => {
    dispatch(setSelectedConversation(user));
    Logger.log("Setting selected conversation:", user.id);
  };

  // Bulk selection handlers
  const handleSelectNext = async (count: number) => {
    Logger.log("Selecting next", count, "conversations");
    toast.loading(`Loading & selecting ${count} conversations...`, { id: 'bulk-select' });
    try {
      const result = await dispatch(selectNextConversations(count)).unwrap();
      toast.success(`Selected ${result.idsToSelect.length} conversations`, { id: 'bulk-select' });
    } catch (error) {
      Logger.log("Error selecting conversations:", error);
      toast.error("Failed to select conversations", { id: 'bulk-select' });
    }
  };

  const handleClearSelection = () => {
    Logger.log("Clearing conversation selection");
    dispatch(clearConversationSelection());
    toast.success("Selection cleared");
  };


  
  const handleStartChat = async (data: { phone: string; name?: string }) => {
    try {
      Logger.log("Starting new chat with:", data);
      
      await dispatch(startNewChat({ 
        phone: data.phone,
        message: "Hello!" 
      })).unwrap();
      
      // Refresh conversations after starting a new chat
      dispatch(fetchConversations({ page: 1, refresh: true }));
      
      toast.success("Chat started successfully");
      dispatch(setShowAddChatModal(false));
    } catch (err) {
      console.error("Failed to start chat:", err);
      toast.error("Failed to start chat");
    }
  };

  // Infinite scrolling with back-to-top button visibility
  const onScrollListing = () => {
    if (listInnerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;

      // Show back-to-top button when scrolled down more than 300px
      setShowBackToTop(scrollTop > 300);

      if (scrollTop + clientHeight >= scrollHeight - 1 && !isLoading) {
        const totalConvo = conversations?.reduce(
          (acc, group) => acc + group.data.length, 0
        ) || 0;

        if (totalConvo < totalConversations) {
          const nextPage = currentPage + 1;
          Logger.log("Loading more conversations, page:", nextPage);
          dispatch(setCurrentPage(nextPage));
          dispatch(fetchConversations({ page: nextPage }));
        }
      }
    }
  };

  // Scroll to top function
  const scrollToTop = () => {
    if (listInnerRef.current) {
      listInnerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="userLists pe-1">
      <div className="chat-list-wrapper">
        {/* Header with title and actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '4px',
          padding: '0 12px'
        }}>
          <span style={{
            fontSize: '20px',
            fontWeight: 600,
            color: '#333',
            fontFamily: 'Gilroy-SemiBold, sans-serif'
          }}>
            Inbox ({totalConversations})
          </span>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Bulk Select Toggle Button */}
            <button
              onClick={() => dispatch(toggleSelectionMode())}
              style={{
                borderRadius: '8px',
                fontWeight: 500,
                height: '32px',
                marginTop: '8px',
                fontFamily: 'Gilroy-Medium, sans-serif',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '0 12px',
                border: isSelectionMode ? '1px solid #dc2626' : '1px solid #e2e8f0',
                background: isSelectionMode ? '#fef2f2' : '#f8fafc',
                color: isSelectionMode ? '#dc2626' : '#374151',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontSize: '13px'
              }}
              title={isSelectionMode ? "Exit selection mode" : "Select multiple"}
              onMouseOver={(e) => {
                if (isSelectionMode) {
                  e.currentTarget.style.background = '#fee2e2';
                } else {
                  e.currentTarget.style.background = '#e0f2fe';
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.color = '#2563eb';
                }
              }}
              onMouseOut={(e) => {
                if (isSelectionMode) {
                  e.currentTarget.style.background = '#fef2f2';
                } else {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.color = '#374151';
                }
              }}
            >
              {isSelectionMode ? (
                <>
                  <X size={15} />
                  Cancel
                </>
              ) : (
                <>
                  <CheckSquare size={15} />
                  Select
                </>
              )}
            </button>

            {/* New Chat Button */}
            <Button
              onClick={() => dispatch(setShowAddChatModal(true))}
              type="primary"
              style={{
                minWidth: '100px',
                borderRadius: '8px',
                fontWeight: 500,
                height: '32px',
                marginTop: '8px',
                fontFamily: 'Gilroy-Medium, sans-serif'
              }}
            >
              New Chat
            </Button>
          </div>
        </div>

        {/* Bulk Selection Controls - shows when in selection mode */}
        {isSelectionMode && (
          <div style={{
            padding: '10px 12px',
            marginBottom: '8px',
            background: 'linear-gradient(135deg, #f0f7ff 0%, #e8f4fd 100%)',
            borderRadius: '10px',
            margin: '8px 12px',
            border: '1px solid #d0e8ff'
          }}>
            {/* Selection info row */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '10px'
            }}>
              <span style={{
                fontFamily: 'Gilroy-SemiBold, sans-serif',
                fontSize: '14px',
                color: '#1e40af',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <CheckSquare size={16} />
                {selectedCount > 0 ? `${selectedCount} selected` : 'Select conversations'}
              </span>
              {selectedCount > 0 && (
                <button
                  onClick={handleClearSelection}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#6b7280',
                    fontSize: '12px',
                    fontFamily: 'Gilroy-Medium, sans-serif',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#fee2e2';
                    e.currentTarget.style.color = '#dc2626';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#6b7280';
                  }}
                >
                  Clear all
                </button>
              )}
            </div>

            {/* Action button */}
            <button
              onClick={() => handleSelectNext(25)}
              disabled={isBulkLoading}
              style={{
                width: '100%',
                padding: '10px 16px',
                background: isBulkLoading ? '#94a3b8' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontFamily: 'Gilroy-SemiBold, sans-serif',
                fontSize: '13px',
                cursor: isBulkLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                boxShadow: isBulkLoading ? 'none' : '0 2px 8px rgba(37, 99, 235, 0.3)'
              }}
              onMouseOver={(e) => {
                if (!isBulkLoading) {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.4)';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = isBulkLoading ? 'none' : '0 2px 8px rgba(37, 99, 235, 0.3)';
              }}
            >
              {isBulkLoading ? (
                <>
                  <span className="loading-spinner" style={{
                    width: '14px',
                    height: '14px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: 'white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Loading & selecting...
                </>
              ) : (
                <>
                  <CheckSquare size={16} />
                  Select next 25 conversations
                </>
              )}
            </button>

            {/* Helper text */}
            <p style={{
              margin: '8px 0 0 0',
              fontSize: '11px',
              color: '#64748b',
              fontFamily: 'Gilroy-Medium, sans-serif',
              textAlign: 'center'
            }}>
              Loads more if needed • Click items to select individually
            </p>
          </div>
        )}



        <AddChat
          visible={showAddChatModal}
          onClose={() => dispatch(setShowAddChatModal(false))}
          onStartChat={handleStartChat}
        />

        {/* Search & Filter Card */}
        <div className="search-filter-card">
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div className="search-wrapper" style={{ flex: 1 }}>
              <Search className="search-icon" size={16} strokeWidth={2} />
              <input
                ref={inputRef}
                className="search-input"
                placeholder="Type to search..."
                name="search"
                id="searchInput"
                onChange={handleFilter}
                value={searchValue}
                type="text"
              />
              {searchValue && (
                <button
                  className="clear-btn"
                  onClick={() => {
                    // Cancel any pending debounced search
                    if (debouncedFunctionRef.current?.cancel) {
                      debouncedFunctionRef.current.cancel();
                    }

                    // Clear local state immediately for UI feedback
                    setSearchValue("");

                    // Clear Redux state immediately (no debounce for clear)
                    dispatch(setFilter({ key: 'search' as any, value: "" }));

                    // Clear input element (backup)
                    clearInputValueById("searchInput");
                  }}
                >
                  <X size={16} strokeWidth={2.5} />
                </button>
              )}
            </div>

            {/* Filter Dropdown */}
            <FilterDropdown />
          </div>
        </div>

        {/* Chat List */}
        <div className="flex-fill position-relative">
          <div className="userListItems">
            {conversations?.length ? (
              <ul
                className="chat_list_container"
                ref={listInnerRef}
                onScroll={onScrollListing}
              >
            <InboxListRedux
  onConnect={onConnect} // ← NOW USE THE DESTRUCTURED PROP
/>
              </ul>
            ) : (
              <div className="h-100 d-flex align-items-center justify-content-center">
                <h5 className="text-dark fs-6 text-center" style={{
                  fontFamily: 'Gilroy-Medium, sans-serif'
                }}>
                  {isLoading ? "Loading..." : "No results found"}
                </h5>
              </div>
            )}

            {/* Back to Top Button */}
            <button
              className={`back-to-top-btn ${showBackToTop ? 'visible' : ''}`}
              onClick={scrollToTop}
              aria-label="Scroll to top"
            >
              <ArrowUp size={20} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar - Fixed at bottom when items selected */}
      {selectedCount > 0 && (
        <ConversationBulkActionsBar selectedCount={selectedCount} />
      )}
    </div>
  );
};

export default ChatLeftMenuRedux;